import { $$ } from "@tento-ui/fastdom";
import { platformOf } from "@tento-lona/host";
import { type Option, RowKey, type RowLocalId } from "@tento-lona/sheets";
import { UtcDays } from "@tento-lona/sheets/internal-ui";
import { RowDemand } from "@tento-lona/sheets/viewport";
import { type SheetRow, SheetViewModel } from "@tento-lona";
import type { FixedChildrenLifetime } from "./fixed-children-lifetime.ts";
import { FixedChildrenQueue } from "./fixed-children-queue.ts";
import { SheetElementFlags } from "./sheet-element-flags.ts";
import { DateTime, NaiveDate, Utc } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import { LiveData } from "@tento-core/livedata";
import { Tracing } from "@tento-core/tracing";
import { FrameMarks } from "@tento-ui/frame-marks";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import { SheetLayoutPort } from "@tento-ui/components/sheet/sheet-layout";
import { ViewportRange } from "@tento-ui/components/sheet/viewport-range";
import { BindGeneration } from "@tento-scroll/engine/scroll-engine";
import type { ScrollEngine } from "@tento-scroll/engine/scroll-engine";
import { DefaultBinderHelpers } from "./binder/default-binder-helpers.ts";
import { FixedChildRowContext, type RenderPlugin, RenderPluginManager } from "./render-plugin.ts";
import { SheetDelegate } from "./sheet-delegate.ts";
import { SheetBinder } from "./sheet-index-renderer.ts";
import { SheetLegend } from "./sheet-legend.ts";

/**
 * The sheet element's emit handle (F11): the visible / rendered column emits
 * (deferred to the next read phase, latest wins, bind-generation guarded),
 * the per-row plugin hooks, fixed-children kickoff and static-child reconcile.
 * Moved out of sheet-element.ts; the element is read through host accessors
 * when a method runs, and the handle's own state (`EmitHandleState`) is
 * written here only.
 */

/** A visible-columns emit deferred to the next read phase. */
export type PendingVisibleEmit = { columns: ColumnIndex.Key.Resolved.Default.Range; source: ScrollEngine.ViewportChangeSource; gen: number };
/** A rendered-columns emit deferred to the next read phase. */
export type PendingRenderedEmit = { columns: ColumnIndex.Key.Resolved.Default.Range; gen: number };

/**
 * The emit loop's own state: the deferred emits, the last emitted visible
 * range and the revealed static children. Owner: this module; the element
 * reads `activeStaticChildren` and forgets the visible range on a bind.
 */
export class EmitHandleState {
  /** The latest deferred visible-columns emit. */
  pendingVisibleEmit: Option<PendingVisibleEmit> = null;
  /** Whether a visible emit is queued for the next read phase. */
  visibleEmitScheduled = false;
  /** The latest deferred rendered-columns emit. */
  pendingRenderedEmit: Option<PendingRenderedEmit> = null;
  /** Whether a rendered emit is queued for the next read phase. */
  renderedEmitScheduled = false;
  /** The visible date range last emitted. */
  lastVisibleColumnsKey: Option<string> = null;
  /** Static child ids revealed per row. */
  activeStaticChildren: Map<string, Set<string>> = new Map();

  /** Forget the last emitted visible range, so the next visible emit is not deduplicated against it. */
  forgetVisibleColumns(): void {
    this.lastVisibleColumnsKey = null;
  }

  /** Forget every row's revealed static children (a presentation reset dismissed them). */
  forgetStaticChildren(): void {
    this.activeStaticChildren = new Map();
  }
}

/** What the handle reads on its sheet element (when a method runs), and the state it writes. */
export type EmitHandleHost = {
  /** The bound sheet, if any. */
  readonly sheet: Option<SheetViewModel.Surface<SheetBinder>>;
  /** The layout surface the sheet drives. */
  readonly layout: SheetLayoutPort;
  /** The emit loop's state; written here only. */
  readonly state: EmitHandleState;
  /** When the latest bind started. */
  readonly lastBindStartedAtMs: number;
  /** The element's bind generation. */
  readonly bindGeneration: BindGeneration;
  /** The render plugins. */
  readonly pluginManager: Option<RenderPluginManager>;
  /** The body legend. */
  readonly legend: SheetLegend;
  /** The header legend. */
  readonly headerLegend: SheetLegend;
  /** The element's default binder. */
  readonly defaultBinder: SheetBinder;
  /** The sheet's clock. */
  readonly currentTime: LiveData<DateTime<Utc>>;
  /** Start a fixed-child request for `key` (row and owner): returns its generation, superseding earlier ones. */
  nextSpecGeneration(key: string): number;
  /** Whether `gen` is still the latest request generation for `key`. */
  isCurrentSpecGeneration(key: string, gen: number): boolean;
  /** The fixed-children requests' lifecycle ticket. */
  readonly fixedChildrenLifetime: FixedChildrenLifetime;
  /** Per-row fixed-children applies under the frame budget (`scroll-frame-deadline`). */
  readonly fixedChildrenQueue: FixedChildrenQueue;
  /** The element's delegate handle. */
  readonly handle: SheetDelegate;
  /** The decorations. */
  readonly decorationsHandle: { rebind(): void };
  /** The layout debug overlay. */
  readonly layoutDebugOverlayHandle: { rebind(): void };
  /** The fixed-child handle. */
  readonly fixedChildHandle: SheetDelegate.FixedChildHandle;
  /** The static-child handle. */
  readonly staticChildHandle: SheetDelegate.StaticChildHandle;
  /** The state handle. */
  readonly stateHandle: SheetDelegate.State;
  /** The focus callback. */
  readonly onFocusCell: Option<(cell: SheetCell.CellId) => void>;
  /** The blur callback. */
  readonly onBlurCell: Option<(cell: SheetCell.CellId) => void>;
  /** The viewport-change callback. */
  readonly onViewportChanged: Option<(columns: ColumnIndex.Key.Resolved.Default.Range, viewportChangeSource: ScrollEngine.ViewportChangeSource) => void>;
  /** The resolved cell width in pixels. */
  readonly resolvedCellWidthPx: Option<number>;
  /** Report a missing row list. */
  readonly assertrl: () => void;
  /**
   * The residency the rendered emit's per-row pass follows (`RowDemand`), read
   * when a pass runs; `null` when the sheet is not windowed (every row).
   */
  readonly residency: () => Option<EmitResidency>;
};

/** A windowed sheet's residency, as the rendered emit reads it. */
export type EmitResidency = {
  /** The sheet reads the demand walks (`platformOf(handle).sheet` in production). */
  readonly reads: RowDemand.Sheet;
  /** The rows the row viewport holds resident now. */
  readonly resident: readonly RowLocalId[];
};

/** The row loads a rendered emit kicks off (`platformOf(handle)` in production). */
export type RowLoads = {
  ensureRowsLoaded(range: NaiveDate.Range): Promise<void>;
  ensureRootsLoaded(range: NaiveDate.Range, roots: readonly RowLocalId[]): Promise<void>;
};

/**
 * The UTC days the cells of `columns` live in: the range the rendered emit's
 * sheet-wide loads (`startFetch`, `loadRenderedRows`) name. The server reads
 * cells by UTC day and a column is a day of the viewer's zone, so west of UTC
 * the last column ends past a UTC midnight and east of UTC the first one
 * starts before one. Each column's own load names the UTC days its instants
 * touch (`RowEvents.ensureLoaded`); the sheet-wide loads name the same days
 * for the whole strip: from the date the first column starts on (in UTC) to
 * the date the last one ends on, plus that day when it ends past its
 * midnight. Otherwise a column at the edge of what the sheet's prefetch
 * covered asks for one day outside it, and its aggregate posts a padded batch
 * of its own in the middle of a scroll. For a UTC viewer this is
 * `toDatesInclusive()`.
 */
export function renderedLoadDays(columns: ColumnIndex.Key.Resolved.Default.Range): NaiveDate.Range {
  return UtcDays.touched(new DateTime.Range(columns.start.range.start, columns.end.range.end));
}

/**
 * Load the rendered range's rows: on a windowed sheet, the trees of the
 * resident rows' demanded roots only (`RowDemand.roots`, one batched load per
 * root, no other row); on an unwindowed sheet (`residency` null), every row's
 * aggregates in one batch.
 */
export function loadRenderedRows(loads: RowLoads, range: NaiveDate.Range, residency: Option<EmitResidency>): Promise<void> {
  if (residency == null) return loads.ensureRowsLoaded(range);
  return loads.ensureRootsLoaded(range, RowDemand.roots(residency.reads, residency.resident));
}

/**
 * The rows of `rows` a rendered emit runs its per-row plugin pass for: those
 * whose root row is in the SDK's load demand of the resident rows
 * (`RowDemand.roots`; a root row's tree loads as one, so every row under a
 * demanded root is demanded), plus any resident row the reads place under no
 * root (it demands no load, but it is on screen). `null` = every row (an
 * unwindowed sheet).
 */
export function demandedRows(
  rows: readonly { readonly id: RowLocalId }[],
  residency: Option<EmitResidency>,
): Option<ReadonlySet<RowLocalId>> {
  if (residency == null) return null;
  const { reads, resident } = residency;
  const roots = new Set(RowDemand.roots(reads, resident));
  const demanded = new Set<RowLocalId>(resident);
  for (const row of rows) {
    // The row's ancestors, up to a demanded root or out of the tree
    // (unknown, detached or cyclic: not demanded unless resident).
    const seen = new Set<RowLocalId>();
    for (let at: Option<RowLocalId> = row.id; at != null && !seen.has(at); at = reads.parentOf(at)) {
      if (roots.has(at)) {
        demanded.add(row.id);
        break;
      }
      seen.add(at);
    }
  }
  return demanded;
}

/** The handle the element keeps as `#emitHandle`. */
export function makeEmitHandle(host: EmitHandleHost) {
  // The last rendered emit: its columns, bind generation and the rows its
  // per-row pass ran for (`null` = every row). A residency change runs the
  // pass for the rows the new demand adds (`demandChanged`).
  let lastRendered: Option<{
    readonly columns: ColumnIndex.Key.Resolved.Default.Range;
    readonly gen: BindGeneration;
    emitted: Option<ReadonlySet<RowLocalId>>;
  }> = null;
  const handle = {
      visible(
        columns: Option<ColumnIndex.Key.Resolved.Default.Range> =
          host.layout.columns.visible ?? null,
        viewportChangeSource: ScrollEngine.ViewportChangeSource = "bind",
      ) {
        if (!host.sheet) return host.assertrl();
        if (!columns) return;
        host.state.pendingVisibleEmit = {
          columns,
          source: viewportChangeSource,
          gen: host.bindGeneration,
        };
        if (host.state.visibleEmitScheduled) return;
        host.state.visibleEmitScheduled = true;
        $$.measure(() => {
          host.state.visibleEmitScheduled = false;
          const pending = host.state.pendingVisibleEmit;
          host.state.pendingVisibleEmit = null;
          if (!pending || pending.gen !== host.bindGeneration) return;
          handle.runVisibleEmit(pending.columns, pending.source);
        });
      },
      runVisibleEmit(
        columns: ColumnIndex.Key.Resolved.Default.Range,
        viewportChangeSource: ScrollEngine.ViewportChangeSource,
      ) {
        if (!host.sheet) return;
        const visibleDates = columns.toDatesInclusive();
        const visibleKey = `${visibleDates.start.toString()}..${visibleDates.end.toString()}`;
        if (host.state.lastVisibleColumnsKey === visibleKey) {
          // Early return within 3s of a bind: a rebuilt legend may have missed its
          // paint pass. Windowed so the steady-state scroll path stays silent.
          if (performance.now() - host.lastBindStartedAtMs < 3000) {
            Tracing.log("header/visible-early-return", {
              visibleKey,
              gen: host.bindGeneration,
            });
          }
          return;
        }
        host.state.lastVisibleColumnsKey = visibleKey;
        Tracing.info("ui")("core/sheet/bind/on-visible-columns");
        const loopStartedAtMs = performance.now();
        const frameDeadline = SheetElementFlags.frameDeadline();
        const sheetAtEmit = host.sheet;
        const genAtEmit = host.bindGeneration;
        const atEmit = () => host.sheet === sheetAtEmit && host.bindGeneration === genAtEmit;
        // This emit runs in fastdom's read phase. Under the frame deadline its
        // DOM writes are budgeted write-phase units; the static-span apply is
        // collected and flushed as one write-phase task in both modes, so it
        // never precedes the frame's later layout reads (the row viewport's
        // scroll top). Without the deadline the per-row applies stay synchronous.
        const writes: Array<() => void> = [];
        // Iterate the body-visible projection — `tree.rows` includes
        // subsumed / alias-internal rows whose plugin can't bind their
        // ancestor row class (e.g. per-calendar `data-source` children
        // under a calendar-list `events` aggregator have no
        // `CalendarRenderer` child and would crash
        // `resolvePluginRow`). Visible-body hooks only need the
        // user-visible rows.
        for (const row of host.sheet.joinedTree.flattened.rows) {
          const apply = () => {
            const binding = DefaultBinderHelpers.resolveCellBindingOrSkip(
              row,
              nonnull(host.pluginManager, "Sheet: pluginManager not set"),
            );
            if (!binding) return;
            const plugin = binding.plugin;
            let $legend =
              host.headerLegend.$children.get(row.id)?.$content ??
              host.legend.$children.get(row.id)?.$content ?? null;
            plugin.onViewportColumnsChanged(
              row,
              columns,
              host.handle,
              $legend,
              viewportChangeSource,
            );
          };
          // Under the frame deadline each row's hook is a budgeted unit,
          // queued ahead of that row's fixed-children re-cull below.
          if (frameDeadline) {
            host.fixedChildrenQueue.enqueue({
              key: `${row.id}::viewport-columns`,
              rowId: row.id,
              apply: () => {
                if (atEmit()) apply();
              },
            });
          } else apply();
        }
        // Update static children for spans that exceed the visible viewport
        writes.push(() => host.defaultBinder.updateStaticChildSpans?.(sheetAtEmit, columns));
        host.decorationsHandle.rebind();
        host.layoutDebugOverlayHandle.rebind();

        // Re-cull registered specs for the rendered range
        const visibleRange = host.layout.columns.renderedIndices();
        for (const compositeKey of host.fixedChildHandle.specRegistryKeys()) {
          const [rowIdStr, owner] = compositeKey.split("::");
          if (rowIdStr === undefined || owner === undefined) continue;
          const rowLocalId = nonnull(host.sheet, "Sheet: no sheet bound").ids.resolve(
            RowKey.of(rowIdStr),
          );
          const apply = () => {
            const visibleSpecs = host.fixedChildHandle.reconcileSpecs(
              rowLocalId,
              owner,
              visibleRange,
            );
            host.fixedChildHandle.setFixedChildrenForRow(rowLocalId, visibleSpecs, {
              owner,
            });
          };
          // Under the frame deadline each row is a budgeted unit: visible
          // rows first, at least one per frame, the rest in later frames.
          if (frameDeadline) {
            host.fixedChildrenQueue.enqueue({
              key: compositeKey,
              rowId: rowLocalId,
              apply: () => {
                if (atEmit()) apply();
              },
            });
          } else apply();
        }
        if (writes.length > 0) {
          $$.mutate(() => {
            if (!atEmit()) return;
            for (const write of writes) write();
          });
        }
        // Cost of the synchronous O(rows) visible-columns loop + spec re-cull.
        Tracing.log("sheet/emit/visible", {
          ms: performance.now() - loopStartedAtMs,
          rows: host.sheet.joinedTree.flattened.rows.length,
          gen: host.bindGeneration,
          sheetId: host.sheet.handle.network.id,
        });
        host.onViewportChanged?.(columns, viewportChangeSource);
      },
      rendered(
        columns: Option<ColumnIndex.Key.Resolved.Default.Range> =
          host.layout.columns.rendered ?? null,
      ) {
        if (!host.sheet) return host.assertrl();
        if (!columns) return;
        const renderedRange = columns.toDatesInclusive();
        // The tripwire, before anything spends the range. A rendered strip that
        // is centuries off the calendar is not a deep scroll, it is a strip whose
        // indices were minted in one mapper's space and resolved through
        // another's — and everything below takes the dates on trust and asks the
        // server for them, which is how `end=-395-01-29` became an HTTP 500 and a
        // `parse/yr` throw on the way back. Off the axis: say so, fetch nothing.
        // The plugin/fixed-child body below is skipped with it — binding columns
        // to impossible dates is the same mistake one layer down.
        if (
          !ViewportRange.onAxis(
            renderedRange,
            host.currentTime.get().date,
            ViewportRange.YEARS_TOLERANCE,
          )
        ) {
          Tracing.log("witness/viewport-range", {
            sheetId: host.sheet.handle.network.id,
            start: renderedRange.start.toString(),
            end: renderedRange.end.toString(),
            renderedIdx: [columns.start.offset, columns.end.offset],
            mapperType: columns.start.pd.type,
          });
          return;
        }
        // The fetch kick stays synchronous — deferring it would add a frame of
        // latency to every data arrival. The O(rows) plugin/fixed-child body
        // below defers to the next read phase (see the pending-emit note above).
        const loadDays = renderedLoadDays(columns);
        platformOf(host.sheet.handle).startFetch(loadDays);
        // Coordinate the rows-side `ListDataSource` aggregates: batched
        // POSTs covering the rendered range (a windowed sheet's demanded
        // trees only), so per-aggregate `ensureLoaded` calls during plugin
        // bind hit cache. Idempotent on overlapping ranges.
        void loadRenderedRows(platformOf(host.sheet.handle), loadDays, host.residency());
        host.state.pendingRenderedEmit = { columns, gen: host.bindGeneration };
        if (host.state.renderedEmitScheduled) return;
        host.state.renderedEmitScheduled = true;
        $$.measure(() => {
          host.state.renderedEmitScheduled = false;
          const pending = host.state.pendingRenderedEmit;
          host.state.pendingRenderedEmit = null;
          if (!pending || pending.gen !== host.bindGeneration) return;
          handle.runRenderedEmit(pending.columns);
        });
      },
      runRenderedEmit(columns: ColumnIndex.Key.Resolved.Default.Range) {
        const sheet = host.sheet;
        if (!sheet) return;
        Tracing.info("ui")("core/sheet/bind/on-rendered-columns");
        // Same body-visible projection as the visible() hook above —
        // skips alias-internal source rows that have no
        // resolvable plugin row class — limited to the rows the resident
        // viewport demands (`demandedRows`).
        const rows = sheet.joinedTree.flattened.rows;
        const emitted = demandedRows(rows, host.residency());
        lastRendered = { columns, gen: host.bindGeneration, emitted };
        const loopStartedAtMs = performance.now();
        const passed = emitted == null ? rows : rows.filter((row) => emitted.has(row.id));
        const visited = renderedPass(columns, passed);
        // Cost of the synchronous O(rows) rendered-columns loop (fixed-child kickoff).
        Tracing.log("sheet/emit/rendered", {
          ms: performance.now() - loopStartedAtMs,
          rows: rows.length,
          demanded: passed.length,
          visited,
          gen: host.bindGeneration,
          sheetId: sheet.handle.network.id,
        });
      },
      /**
       * The row viewport's residency changed (a vertical scroll, a resize, a
       * focus pin, a sheet rebind in the same bind generation): run the last
       * rendered emit's per-row pass, over its columns, for the rows the new
       * demand adds. A row that left the demand keeps what it holds and is not
       * emitted again until it returns (`RowDemand`'s offscreen freshness). A
       * queued rendered emit, or a newer bind generation, reads the new demand
       * itself.
       */
      demandChanged() {
        const sheet = host.sheet;
        const last = lastRendered;
        if (!sheet || last == null || last.gen !== host.bindGeneration || host.state.pendingRenderedEmit != null) return;
        const rows = sheet.joinedTree.flattened.rows;
        const previous = last.emitted;
        const next = demandedRows(rows, host.residency());
        last.emitted = next;
        if (previous == null) return;
        const added = rows.filter((row) => (next == null || next.has(row.id)) && !previous.has(row.id));
        if (added.length === 0) return;
        const loopStartedAtMs = performance.now();
        const visited = renderedPass(last.columns, added);
        Tracing.log("sheet/emit/demand", {
          ms: performance.now() - loopStartedAtMs,
          added: added.length,
          visited,
          gen: host.bindGeneration,
          sheetId: sheet.handle.network.id,
        });
      },
      onBindRow(sheet: Option<SheetViewModel.Surface<SheetBinder>> = host.sheet) {
        if (!sheet) return;
        Tracing.info("ui")("core/sheet/bind/on-bind-row");
        const loopStartedAtMs = performance.now();
        for (const row of sheet.joinedTree.flattened.rows) {
          const binding = DefaultBinderHelpers.resolveCellBindingOrSkip(
            row,
            nonnull(host.pluginManager, "Sheet: pluginManager not set"),
          );
          if (!binding) continue;
          binding.plugin.onBindRow(host.handle, row);
        }
        // Cost of the synchronous O(rows) onBindRow loop.
        Tracing.log("sheet/emit/on-bind-row", {
          ms: performance.now() - loopStartedAtMs,
          rows: sheet.joinedTree.flattened.rows.length,
          gen: host.bindGeneration,
          sheetId: sheet.handle.network.id,
        });
      },
      reconcileStaticChildren(
        sheet: Option<SheetViewModel.Surface<SheetBinder>> = host.sheet,
      ) {
        if (!sheet) return;
        const loopStartedAtMs = performance.now();
        const nextActive = new Map<string, Set<string>>();
        const revealed: string[] = [];
        const dismissed: string[] = [];

        for (const row of sheet.joinedTree.flattened.rows) {
          const binding = DefaultBinderHelpers.resolveCellBindingOrSkip(
            row,
            nonnull(host.pluginManager, "Sheet: pluginManager not set"),
          );
          if (!binding) continue;
          const plugin = binding.plugin;
          const specs = plugin.staticChildren?.(row, host.handle) ?? [];
          if (specs.length === 0) continue;

          const rowKey = row.id.toString();
          const ids = new Set<string>();
          for (const spec of specs) {
            ids.add(spec.id);
            revealed.push(spec.id);
            host.staticChildHandle.reveal(spec.id, spec.$element, row.id, {
              ...(spec.align != null ? { align: spec.align } : {}),
              fillWidth: spec.fillWidth ?? false,
            });
          }
          nextActive.set(rowKey, ids);
        }

        // Dismiss stale: any static child that was active before but isn't now
        for (const [rowKey, prevIds] of host.state.activeStaticChildren) {
          const nextIds = nextActive.get(rowKey);
          for (const id of prevIds) {
            if (!nextIds?.has(id)) {
              dismissed.push(id);
              const rowId = sheet.ids.resolve(RowKey.of(rowKey));
              host.staticChildHandle.dismiss(id, rowId);
            }
          }
        }

        host.state.activeStaticChildren = nextActive;
        // Cost of the synchronous O(rows × specs) static-child reconcile + dismissals.
        Tracing.log("sheet/emit/reconcile-static", {
          ms: performance.now() - loopStartedAtMs,
          rows: sheet.joinedTree.flattened.rows.length,
          revealed: revealed.length,
          dismissed: dismissed.length,
          gen: host.bindGeneration,
          sheetId: sheet.handle.network.id,
        });
      },
  };
  /**
   * The rendered emit's per-row pass over `rows` for `columns`: each row's
   * plugin hears the columns change and kicks off its fixed children (run, or
   * held for release, as one pass). Returns how many rows it ran for.
   */
  function renderedPass(
    columns: ColumnIndex.Key.Resolved.Default.Range,
    rows: readonly SheetRow.Joined.Handle[],
  ): number {
    // `bandRowId` is added per-row by `bindFixedChildren` (it knows the
    // band); everything else is shared across rows.
    const buildFixedChildContext = (): Omit<
      FixedChildRowContext,
      "bandRowId"
    > => ({
      renderedColumns: columns,
      sheet: nonnull(host.sheet, "Sheet: no sheet bound"),
      handle: host.handle,
      // Source the mapper from the SAME SheetViewModel as `sheet` above so
      // the two are always consistent. The LAYOUT's own mapper
      // (`SheetLayoutPort.space`) lags during a timescale/view-config
      // transition — `host.sheet` is swapped early in bind() but the layer's
      // space only updates later in bindSheet()'s mutate, which is why this
      // reads the view model's mapper rather than the layer's. A render emit
      // in that window read `timeScale="day"` with a stale week-granularity
      // mapper, so the timeline-legend resolved a 7-day `pd` and crammed a
      // week of ticks into one day-column (overlap that persisted until
      // reload). The
      // SheetViewModel's own mapper always matches its `timeScale`.
      mapper: nonnull(host.sheet, "Sheet: no sheet bound").indexRenderer.mapper,
      now: host.currentTime.get(),
      resolvedWidthPx: nonnull(host.resolvedCellWidthPx, "Sheet: cell width not resolved"),
      visibleWidthPx: host.stateHandle.visibleWidthPx ?? 0,
      onFocusCell: host.onFocusCell
        ? (cell) => host.onFocusCell?.(cell)
        : null,
      onBlurCell: host.onBlurCell
        ? (cell) => host.onBlurCell?.(cell)
        : null,
    });

    // Run one plugin's fixedChildrenForRow and attach its specs to
    // `bandRow`'s rendered band. `paramRow` is what the plugin sees
    // (drives discovery + span keying); `bandRow` owns the cull-layer
    // band the specs render into. For an own-row binding the two match.
    const bindFixedChildren = (
      fcPlugin: Pick<RenderPlugin.Resolved, "fixedChildrenForRow">,
      paramRow: SheetRow.Joined.Handle,
      bandRow: SheetRow.Joined.Handle,
      owner: string,
    ): void => {
      const ask = fcPlugin.fixedChildrenForRow;
      if (!ask) return;
      const bandRowId = bandRow.id;
      const genKey = `${bandRowId.toString()}::${owner}`;
      const gen = host.nextSpecGeneration(genKey);
      const sheet = host.sheet; // capture for closure
      // Ownership at kickoff: a completion applies only to the sheet and
      // bind it was requested for (spec generations restart on rebind, so
      // the numeric check alone cannot tell an old bind's request apart).
      const bindGen = host.bindGeneration;
      // ...and its lifecycle ticket: a detach, recycle or rebind before this
      // request resolves retires it, even if the same sheet is reconnected
      // under the same bind by then.
      // The ticket is checked when the provider resolves and again when the
      // apply runs, on both paths (applied now, or a budgeted unit under the
      // frame deadline): a queue cleared on detach does not stop a completion
      // that enqueues after the clear.
      const ticket = host.fixedChildrenLifetime.ticket();
      const live = () => host.fixedChildrenLifetime.isCurrent(ticket);
      // The kickoff, marked `fixed.kickoff` (counts -1 and 1: the whole
      // rendered range; col = the columns asked).
      const mark = FrameMarks.begin();
      let requested: Promise<FixedChildSpecOf[]>;
      try {
        requested = ask.call(fcPlugin, paramRow, {
          ...buildFixedChildContext(),
          bandRowId,
        });
      } finally {
        // A fixture's range stub may carry no ends: NaN.
        const asked = columns.start?.offset != null && columns.end?.offset != null ? columns.end.offset - columns.start.offset + 1 : NaN;
        FrameMarks.end("fixed.kickoff", mark, -1, 1, asked);
      }
      requested.then((specs) => {
        if (!live()) return;
        const apply = () => {
          if (!live()) return;
          if (!sheet || host.sheet !== sheet) return;
          if (host.bindGeneration !== bindGen) return;
          // Discard stale async completions
          if (!host.isCurrentSpecGeneration(genKey, gen)) return;
          host.fixedChildHandle.registerSpecs(bandRowId, owner, specs);
          const visibleRange = host.layout.columns.renderedIndices();
          const visibleSpecs = host.fixedChildHandle.reconcileSpecs(
            bandRowId,
            owner,
            visibleRange,
          );
          host.fixedChildHandle.setFixedChildrenForRow(
            bandRowId,
            visibleSpecs,
            { owner },
          );
        };
        // Under the frame deadline a completion is a budgeted unit; off, it
        // applies now.
        if (SheetElementFlags.frameDeadline()) {
          host.fixedChildrenQueue.enqueue({ key: genKey, rowId: bandRowId, apply });
        } else apply();
      }, (error: unknown) => {
        // A rejected provider applies nothing: the row keeps what it shows
        // and its next emit asks again. Nothing waits on this request, so
        // there is nothing to release.
        Tracing.log("sheet/fixed-children-error", { rowId: String(bandRowId), owner, error: String(error) });
      });
    };

    // One row's part of the pass: its rendered-columns hook (marked
    // `emit.rendered-row` part 0 at `place`), and its fixed-children kickoffs
    // pushed onto `kickoffs`. False when the row has no binding.
    const passRow = (row: SheetRow.Joined.Handle, place: number, kickoffs: Array<() => void>): boolean => {
      const binding = DefaultBinderHelpers.resolveCellBindingOrSkip(
        row,
        nonnull(host.pluginManager, "Sheet: pluginManager not set"),
      );
      if (!binding) return false;
      const plugin = binding.plugin;
      const hookMark = FrameMarks.begin();
      try {
        plugin.onRenderedColumnsWillChange(row, columns, host.handle);
      } finally {
        FrameMarks.end("emit.rendered-row", hookMark, place, 0);
      }

      // The row's own plugin paints its fixed children onto its own band.
      kickoffs.push(() =>
        bindFixedChildren(
          plugin,
          DefaultBinderHelpers.fixedChildrenRenderRow(row, binding),
          row,
          binding.pluginId,
        ));

      // A layout row additionally binds its `<layout>.event` renderer
      // onto its own band. That renderer's row (e.g. the all-day strip's
      // `events` source under `timeline[row]`) is alias-internal and
      // never appears in `flattened.rows`, so it would otherwise never
      // paint. Passing the layout row lets the renderer discover its
      // provider by walking down, and keys its spans + gesture context
      // to the same band they attach to.
      if (plugin.role === "layout") {
        const eventPlugin = nonnull(host.pluginManager, "Sheet: pluginManager not set").getPlugin(
          `${binding.pluginId}.event`,
          "renderer",
        );
        if (
          eventPlugin.role === "renderer" &&
          eventPlugin.fixedChildrenForRow
        ) {
          kickoffs.push(() => bindFixedChildren(eventPlugin, row, row, eventPlugin.id));
        }
      }
      return true;
    };

    // The fixed-children part of this emit is collected and run as one pass.
    const fixedChildrenPass: Array<() => void> = [];
    // Frame mark `emit.rendered-pass` (`frame-marks` debug flag): the pass's
    // synchronous part, with the rows it was handed and the rows it ran for;
    // inside it, `emit.rendered-row` per row: its rendered-columns hook (part
    // 0) and each of its fixed-children kickoffs (part 1), with the row's
    // place in the pass, so a long pass names the row that cost it
    // (forward max frames held 7-15 ms passes; one mark per pass could not
    // say which row).
    const mark = FrameMarks.begin();
    let ran = 0;
    /** `kickoff` as a pass step, marked `emit.rendered-row` part 1 for the row at `place`. */
    const markedKickoff = (place: number, kickoff: () => void) => () => {
      const kickoffMark = FrameMarks.begin();
      try {
        kickoff();
      } finally {
        FrameMarks.end("emit.rendered-row", kickoffMark, place, 1);
      }
    };
    try {
      rows.forEach((row, place) => {
        const kickoffs: Array<() => void> = [];
        if (!passRow(row, place, kickoffs)) return;
        ran++;
        for (const kickoff of kickoffs) fixedChildrenPass.push(markedKickoff(place, kickoff));
      });
      for (const step of fixedChildrenPass) step();
    } finally {
      FrameMarks.end("emit.rendered-pass", mark, rows.length, ran);
    }
    return ran;
  }
  return handle;
}

/** A fixed-child spec as a plugin's `fixedChildrenForRow` returns it and `registerSpecs` takes it. */
type FixedChildSpecOf = SheetDelegate.FixedChildSpec;

/** The element's emit handle (`makeEmitHandle`'s result). */
export type EmitHandle = ReturnType<typeof makeEmitHandle>;
